<template>
  <div v-if="!isLoaded" :style="{ height: `${loaderHeight}px` }" class="mt-1">
    <v-row>
      <v-col>
        <v-skeleton-loader
          type="
            table-heading,
            image,
            list-item-two-line,
            list-item-two-line,
            list-item-two-line"
        ></v-skeleton-loader>
      </v-col>
      <v-col>
        <v-skeleton-loader
          type="
            table-heading,
            list-item-two-line,
            list-item-two-line,
            list-item-two-line,
            article"
        ></v-skeleton-loader>
      </v-col>
      <v-col v-if="needAppBlock">
        <v-skeleton-loader
          type="
            table-heading,
            list-item-two-line,
            article,
            list-item-two-line,
            article"
        ></v-skeleton-loader>
      </v-col>
    </v-row>
  </div>
  <v-form
    class="TemplateComposer"
    v-else
    ref="form"
    lazy-validation
    v-model="formValid"
  >
    <v-dialog
      v-model="helpDialog"
      hide-overlay
      width="300"
    >
      <v-alert
        border="top"
        colored-border
        type="info"
        elevation="2"
        class="mb-0 pb-0"
      >
        <div v-if="helpKey === 'build_version'">
          <p>
            {{ $t('definesStartVersionOfYourArtifactEachRunIncrements') }}
          </p>
          <p>
            {{ $t('forMoreInformationAboutBuildingSeeThe') }}
            {{ $t('taskTemplateReference') }}.
          </p>
        </div>
        <div v-else-if="helpKey === 'build'">
          <p>
            {{ $t('definesWhatArtifactShouldBeDeployedWhenTheTaskRun') }}
          </p>
          <p>
            {{ $t('forMoreInformationAboutDeployingSeeThe') }}
            {{ $t('taskTemplateReference2') }}.
          </p>
        </div>
        <div v-else-if="helpKey === 'cron'">
          <p>{{ $t('definesAutorunSchedule') }}</p>
          <p>
            {{ $t('forMoreInformationAboutCronSeeThe') }}
            <a href="https://pkg.go.dev/github.com/robfig/cron/v3#hdr-CRON_Expression_Format"
               target="_blank"
            >{{ $t('cronExpressionFormatReference') }}</a>.
          </p>
        </div>
      </v-alert>
    </v-dialog>

    <v-alert
      :value="formError"
      color="error"
      class="TemplateComposer__alert"
    >{{ formError }}
    </v-alert>

    <div class="TemplateComposer__grid">
      <aside class="TemplateComposer__rail">
        <div class="TemplateComposer__module">
          <span class="zp-overline">{{ $t('templateComposerModule') }}</span>
          <div class="TemplateComposer__app">
            <v-icon :color="appIconColor" size="20">{{ getAppIcon(app) }}</v-icon>
            <div>
              <strong>{{ appTitle }}</strong>
              <span>{{ selectedTypeTitle }}</span>
            </div>
          </div>
        </div>

        <div
          class="TemplateComposer__steps"
          :aria-label="$t('templateComposerSections')"
          role="list"
        >
          <div
            v-for="section in formSections"
            :key="section.id"
            class="TemplateComposer__step"
            :class="{ 'TemplateComposer__step--complete': section.complete }"
            role="listitem"
          >
            <span class="TemplateComposer__stepGlyph">
              <ZpIcon :name="section.complete ? 'check' : section.icon" :size="14" />
            </span>
            <span>
              <strong>{{ section.title }}</strong>
              <small>{{ section.meta }}</small>
            </span>
          </div>
        </div>
      </aside>

      <main class="TemplateComposer__main">
        <section id="template-identity" class="TemplateComposer__section">
          <header class="TemplateComposer__sectionHead">
            <div>
              <span class="zp-overline">{{ $t('templateComposerStep') }} 01</span>
              <h2>{{ $t('templateComposerIdentity') }}</h2>
            </div>
            <span
              class="zp-status"
              :class="identityComplete ? 'zp-status--success' : 'zp-status--neutral'"
            >
              {{ identityComplete ? $t('ready') : $t('required') }}
            </span>
          </header>

          <div class="TemplateComposer__typeGrid">
            <button
              v-for="(key, index) in typeKeys"
              :key="key || 'task'"
              type="button"
              class="TemplateComposer__type"
              :class="{ 'TemplateComposer__type--active': itemTypeIndex === index }"
              @click="itemTypeIndex = index"
            >
              <ZpIcon :name="TEMPLATE_TYPE_ICONS[key]" :size="16" />
              <span>{{ $t(TEMPLATE_TYPE_TITLES[key]) }}</span>
            </button>
          </div>

          <div class="TemplateComposer__fieldGrid TemplateComposer__fieldGrid--two">
            <v-text-field
              v-model="item.name"
              :label="$t('name2')"
              :rules="[v => !!v || $t('name_required')]"
              outlined
              dense
              required
              :disabled="formSaving"
            ></v-text-field>

            <v-autocomplete
              v-model="item.view_id"
              :label="$t('view')"
              clearable
              :items="views"
              item-value="id"
              item-text="title"
              :disabled="formSaving"
              outlined
              dense
            ></v-autocomplete>
          </div>

          <div
            class="TemplateComposer__fieldGrid TemplateComposer__fieldGrid--two"
            v-if="item.type"
          >
            <v-text-field
              v-if="item.type === 'build'"
              v-model="item.start_version"
              :label="$t('startVersion')"
              :rules="[v => !!v || $t('start_version_required')]"
              required
              outlined
              dense
              :disabled="formSaving"
              :placeholder="$t('example000')"
            >
              <template #append-outer>
                <ZpIcon name="help-circle" :size="18"
                        class="TemplateComposer__helpIcon"
                        :aria-label="$t('helpTooltip')"
                        @click.stop="showHelpDialog('build_version')"
                        @mousedown.prevent />
              </template>
            </v-text-field>

            <v-autocomplete
              v-if="item.type === 'deploy'"
              v-model="item.build_template_id"
              :label="$t('buildTemplate')"
              :items="buildTemplates"
              item-value="id"
              item-text="name"
              :rules="[v => !!v || $t('build_template_required')]"
              required
              outlined
              dense
              :disabled="formSaving"
            >
              <template #append-outer>
                <ZpIcon name="help-circle" :size="18"
                        class="TemplateComposer__helpIcon"
                        :aria-label="$t('helpTooltip')"
                        @click.stop="showHelpDialog('build')"
                        @mousedown.prevent />
              </template>
            </v-autocomplete>

            <v-checkbox
              v-if="item.type === 'deploy'"
              class="TemplateComposer__switch"
              :label="$t('autorun')"
              v-model="item.autorun"
            />
          </div>
        </section>

        <section id="template-source" class="TemplateComposer__section">
          <header class="TemplateComposer__sectionHead">
            <div>
              <span class="zp-overline">{{ $t('templateComposerStep') }} 02</span>
              <h2>{{ $t('templateComposerSource') }}</h2>
            </div>
            <span class="TemplateComposer__readout zp-mono">{{ sourceReadout }}</span>
          </header>

          <div class="TemplateComposer__fieldGrid TemplateComposer__fieldGrid--two">
            <v-autocomplete
              v-model="item.repository_id"
              :label="fieldLabel('repository') + ' *'"
              :items="repositories"
              item-value="id"
              item-text="name"
              :rules="isFieldRequired('repository') ? [v => !!v || $t('repository_required')] : []"
              outlined
              dense
              :required="isFieldRequired('repository')"
              :disabled="formSaving"
              v-if="needField('repository')"
            ></v-autocomplete>

            <v-autocomplete
              v-if="needField('repository') && branches != null"
              clearable
              :items="branches"
              v-model="item.git_branch"
              :label="fieldLabel('branch')"
              outlined
              dense
              :disabled="formSaving"
              :placeholder="$t('branch')"
            ></v-autocomplete>

            <v-text-field
              v-else-if="needField('repository')"
              clearable
              v-model="item.git_branch"
              :label="fieldLabel('branch')"
              outlined
              dense
              :disabled="formSaving"
              :placeholder="$t('branch')"
            ></v-text-field>
          </div>

          <v-combobox
            v-model="item.playbook"
            :label="fieldLabel('playbook')"
            :items="playbookFiles"
            :loading="loadingPlaybookFiles"
            :rules="
                isFieldRequired('playbook')
                ? [v => !!v || $t('playbook_filename_required')]
                : []"
            outlined
            dense
            :required="isFieldRequired('playbook')"
            :disabled="formSaving"
            :placeholder="$t('exampleSiteyml')"
            :hint="playbookFilesError || $t('templateComposerFileHint')"
            persistent-hint
            v-if="needField('playbook')"
          ></v-combobox>

          <div class="TemplateComposer__fieldGrid TemplateComposer__fieldGrid--two">
            <v-autocomplete
              v-model="item.inventory_id"
              :label="fieldLabel('inventory')"
              :items="inventory"
              item-value="id"
              item-text="name"
              outlined
              dense
              required
              :disabled="formSaving"
              v-if="needField('inventory')"
            ></v-autocomplete>

            <v-autocomplete
              v-if="isAnsibleTemplate"
              v-model="item.execution_environment_id"
              :label="$t('executionEnvironmentSingular')"
              :items="executionEnvironments || []"
              item-value="id"
              item-text="name"
              clearable
              outlined
              dense
              :disabled="formSaving"
              :placeholder="$t('executionEnvironmentSingular')"
            ></v-autocomplete>
          </div>

          <v-autocomplete
            v-model="item.environment_ids"
            :label="fieldLabel('environment')"
            :items="environment"
            item-value="id"
            item-text="name"
            :rules="isFieldRequired('environment')
              ? [v => (Array.isArray(v) && v.length > 0) || $t('environment_required')]
              : []"
            multiple
            chips
            deletable-chips
            outlined
            small-chips
            :required="isFieldRequired('environment')"
            :disabled="formSaving"
            v-if="needField('environment')"
          ></v-autocomplete>

          <v-alert
            v-if="isAnsibleTemplate
              && selectedInventoryType === 'dynamic'
              && item.execution_environment_id == null"
            type="warning"
            text
            dense
            class="TemplateComposer__inlineAlert"
          >
            {{ $t('dynamicInventoryRequiresEE') }}
          </v-alert>
        </section>

        <section id="template-runtime" class="TemplateComposer__section">
          <header class="TemplateComposer__sectionHead">
            <div>
              <span class="zp-overline">{{ $t('templateComposerStep') }} 03</span>
              <h2>{{ $t('templateComposerRuntime') }}</h2>
            </div>
            <span class="TemplateComposer__readout zp-mono">{{ runtimeReadout }}</span>
          </header>

          <v-autocomplete
            v-if="premiumFeatures.project_runners"
            v-model="item.runner_tag"
            :items="runnerTags"
            :label="fieldLabel('runner_tag')"
            item-value="tag"
            item-text="tag"
            outlined
            dense
            :disabled="formSaving"
            :placeholder="$t('runner_tag')"
            clearable
          ></v-autocomplete>

          <div class="TemplateComposer__toggleGrid">
            <v-checkbox
              class="TemplateComposer__switch"
              v-model="item.allow_parallel_tasks"
            >
              <template v-slot:label>
                {{ $t('allow_parallel_tasks') }}
              </template>
            </v-checkbox>

            <v-checkbox
              class="TemplateComposer__switch"
              :label="$t('suppressSuccessAlerts')"
              v-model="item.suppress_success_alerts"
            />

            <v-checkbox
              class="TemplateComposer__switch TemplateComposer__switch--wide"
              :label="$t('iWantToRunATaskByTheCronOnlyForForNewCommitsOfSome')"
              v-model="cronVisible"
            />
          </div>

          <div
            class="TemplateComposer__fieldGrid TemplateComposer__fieldGrid--two"
            v-if="cronVisible"
          >
            <v-select
              v-model="cronRepositoryId"
              :label="$t('repository2')"
              :placeholder="$t('cronChecksNewCommitBeforeRun')"
              :rules="[v => !!v || $t('repository_required')]"
              :items="repositories"
              item-value="id"
              item-text="name"
              clearable
              :disabled="formSaving"
              outlined
              dense
            ></v-select>

            <v-select
              v-model="cronFormat"
              :label="$t('checkInterval')"
              :hint="$t('newCommitCheckInterval')"
              item-value="cron"
              item-text="title"
              :items="cronFormats"
              :disabled="formSaving"
              outlined
              dense
            >
              <template #append-outer>
                <ZpIcon name="help-circle" :size="18"
                        class="TemplateComposer__helpIcon"
                        :aria-label="$t('helpTooltip')"
                        @click.stop="showHelpDialog('cron')"
                        @mousedown.prevent />
              </template>
            </v-select>
          </div>

          <div class="TemplateComposer__args">
            <ArgsPicker
              :vars="args"
              @change="setArgs"
              :title="$t('templateComposerCliArgs')"
            />

            <RichEditor
              v-model="argsJson"
              type="json_array"
              class="TemplateComposer__argsJson"
            />
          </div>
        </section>

        <section
          v-if="needAppBlock"
          id="template-app-options"
          class="TemplateComposer__section"
        >
          <header class="TemplateComposer__sectionHead">
            <div>
              <span class="zp-overline">{{ $t('templateComposerStep') }} 04</span>
              <h2>{{ $t('template_app_options', { app: getAppTitle(app, true) }) }}</h2>
            </div>
          </header>

          <ArgsPicker
            v-if="needField('limit')"
            :vars="item.task_params.limit"
            @change="setLimit"
            :title="$t('limit')"
            :arg-title="$t('limit')"
            :add-arg-title="$t('addLimit')"
          />

          <ArgsPicker
            v-if="needField('tags')"
            :vars="item.task_params.tags"
            @change="setTags"
            :title="$t('tags')"
            :arg-title="$t('tag')"
            :add-arg-title="$t('addTag')"
          />

          <ArgsPicker
            v-if="needField('skip_tags')"
            :vars="item.task_params.skip_tags"
            @change="setSkipTags"
            :title="$t('skipTags')"
            :arg-title="$t('tag')"
            :add-arg-title="$t('addSkippedTag')"
          />

          <TemplateVaults
            v-if="needField('vault')"
            :project-id="this.projectId"
            :vaults="vaults"
            @change="setTemplateVaults"
          ></TemplateVaults>

          <v-checkbox
            class="mt-0"
            :label="$t('auto_approve')"
            v-model="item.task_params.auto_approve"
            v-if="needField('auto_approve')"
          />

          <v-checkbox
            class="mt-0"
            :label="$t('terraform_override_backend')"
            v-model="item.task_params.override_backend"
            :true-value="true"
            :false-value="false"
            v-if="needField('override_backend') && premiumFeatures.terraform_backend"
          />

          <v-combobox
            v-model="item.task_params.backend_filename"
            :label="fieldLabel('terraform_backend_filename')"
            :items="terraformBackendFiles"
            outlined
            dense
            :disabled="formSaving || !item.task_params.override_backend"
            placeholder="backend.tf"
            :rules="[v => validateBackendFilename(v) || $t('terraform_invalid_backend_filename')]"
            v-if="needField('backend_filename') && premiumFeatures.terraform_backend"
          ></v-combobox>
        </section>

        <section id="template-launch-policy" class="TemplateComposer__section">
          <header class="TemplateComposer__sectionHead">
            <div>
              <span class="zp-overline">
                {{ $t('templateComposerStep') }} {{ launchPolicyStep }}
              </span>
              <h2>{{ $t('templateComposerLaunchPolicy') }}</h2>
            </div>
            <span class="TemplateComposer__readout zp-mono">
              {{ promptControlReadout }}
            </span>
          </header>

          <div class="TemplateComposer__policyGrid">
            <button
              v-for="control in promptControls"
              :key="control.key"
              type="button"
              class="TemplateComposer__policy"
              :class="{ 'TemplateComposer__policy--active': control.enabled }"
              @click="togglePromptControl(control)"
            >
              <span class="TemplateComposer__policyIcon">
                <ZpIcon :name="control.enabled ? 'check' : control.icon" :size="15" />
              </span>
              <span>
                <strong>{{ control.title }}</strong>
                <small>{{ control.description }}</small>
              </span>
            </button>
          </div>

          <div class="TemplateComposer__policyNote">
            <ZpIcon name="info" :size="14" />
            <span>{{ $t('templateComposerPromptNote') }}</span>
          </div>
        </section>

        <section id="template-survey" class="TemplateComposer__section">
          <header class="TemplateComposer__sectionHead">
            <div>
              <span class="zp-overline">{{ $t('templateComposerStep') }} {{ surveyStep }}</span>
              <h2>{{ $t('surveyVariables') }}</h2>
            </div>
            <span class="TemplateComposer__readout zp-mono">{{ surveyCountLabel }}</span>
          </header>

          <SurveyVars
            :vars="surveyVars"
            @change="setSurveyVars"
          />
        </section>

        <section id="template-review" class="TemplateComposer__section">
          <header class="TemplateComposer__sectionHead">
            <div>
              <span class="zp-overline">{{ $t('templateComposerStep') }} {{ reviewStep }}</span>
              <h2>{{ $t('templateComposerReview') }}</h2>
            </div>
          </header>

          <div class="TemplateComposer__preflightGrid">
            <div
              v-for="check in preflightChecks"
              :key="check.key"
              class="TemplateComposer__preflight"
              :class="`TemplateComposer__preflight--${check.state}`"
            >
              <ZpIcon :name="check.icon" :size="16" />
              <span>
                <strong>{{ check.title }}</strong>
                <small>{{ check.description }}</small>
              </span>
            </div>
          </div>
        </section>
      </main>

      <aside class="TemplateComposer__summary">
        <section class="TemplateComposer__summaryBlock">
          <span class="zp-overline">{{ $t('templateComposerCommand') }}</span>
          <pre class="TemplateComposer__command zp-mono">{{ commandPreview }}</pre>
        </section>

        <section class="TemplateComposer__summaryBlock">
          <span class="zp-overline">{{ $t('templateComposerReadiness') }}</span>
          <ul class="TemplateComposer__checks">
            <li
              v-for="check in readinessChecks"
              :key="check.key"
              :class="{ 'TemplateComposer__check--ok': check.ok }"
            >
              <ZpIcon :name="check.ok ? 'check' : 'circle'" :size="13" />
              <span>{{ check.label }}</span>
            </li>
          </ul>
        </section>

        <section class="TemplateComposer__summaryBlock">
          <span class="zp-overline">{{ $t('templateComposerResources') }}</span>
          <dl class="TemplateComposer__resourceList">
            <div v-for="resource in resourceSummary" :key="resource.label">
              <dt>{{ resource.label }}</dt>
              <dd>{{ resource.value }}</dd>
            </div>
          </dl>
        </section>

        <section class="TemplateComposer__summaryBlock">
          <span class="zp-overline">{{ $t('templateComposerCapabilities') }}</span>
          <div
            v-for="capability in operatorCapabilities"
            :key="capability.label"
            class="TemplateComposer__capability"
            :class="{ 'TemplateComposer__capability--active': capability.enabled }"
          >
            <ZpIcon :name="capability.enabled ? 'check' : 'minus'" :size="13" />
            <span>{{ capability.label }}</span>
          </div>
        </section>

        <section class="TemplateComposer__summaryBlock">
          <span class="zp-overline">{{ $t('templateComposerAutomation') }}</span>
          <div
            v-for="signal in automationSignals"
            :key="signal.label"
            class="TemplateComposer__signal"
          >
            <span>{{ signal.label }}</span>
            <strong>{{ signal.value }}</strong>
          </div>
        </section>

        <section class="TemplateComposer__summaryBlock" v-if="riskHints.length > 0">
          <span class="zp-overline">{{ $t('templateComposerRiskHints') }}</span>
          <div
            v-for="hint in riskHints"
            :key="hint"
            class="TemplateComposer__risk"
          >
            <ZpIcon name="triangle-alert" :size="14" />
            <span>{{ hint }}</span>
          </div>
        </section>
      </aside>
    </div>
  </v-form>
</template>
<style lang="scss">
.CodeMirror-placeholder {
  color: #a4a4a4 !important;
}

.TemplateComposer {
  background: var(--zp-bg-page);
  color: var(--zp-text-primary);
  min-height: 680px;
}

.TemplateComposer__alert {
  margin: 16px 20px 0;
}

.TemplateComposer__grid {
  display: grid;
  grid-template-columns: 240px minmax(0, 1fr) 340px;
  min-height: 680px;
}

.TemplateComposer__rail,
.TemplateComposer__summary {
  background: var(--zp-bg-elevated);
}

.TemplateComposer__rail {
  border-right: 1px solid var(--zp-border-hairline);
  padding: 18px 14px;
}

.TemplateComposer__summary {
  border-left: 1px solid var(--zp-border-hairline);
  padding: 18px 16px;
}

.TemplateComposer__module {
  display: grid;
  gap: 10px;
  padding: 10px 8px 16px;
  border-bottom: 1px solid var(--zp-border-hairline);
}

.TemplateComposer__app {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;

  strong,
  span {
    display: block;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  strong {
    font-size: var(--zp-text-sm);
    line-height: 1.2;
  }

  span {
    color: var(--zp-text-tertiary);
    font-size: var(--zp-text-xs);
    margin-top: 2px;
  }
}

.TemplateComposer__steps {
  display: grid;
  gap: 4px;
  padding-top: 14px;
}

.TemplateComposer__step {
  display: grid;
  grid-template-columns: 24px minmax(0, 1fr);
  align-items: center;
  gap: 8px;
  padding: 8px;
  border-radius: var(--zp-radius-md);
  color: var(--zp-text-secondary);

  strong,
  small {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  strong {
    font-size: var(--zp-text-sm);
    line-height: 1.2;
  }

  small {
    color: var(--zp-text-tertiary);
    font-size: var(--zp-text-xs);
    margin-top: 2px;
  }
}

.TemplateComposer__step--complete .TemplateComposer__stepGlyph {
  color: var(--zp-status-success);
  background: var(--zp-status-success-bg);
  border-color: transparent;
}

.TemplateComposer__stepGlyph {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: 1px solid var(--zp-border-default);
  border-radius: var(--zp-radius-sm);
  color: var(--zp-text-tertiary);
  background: var(--zp-bg-surface);
}

.TemplateComposer__main {
  padding: 18px 20px 24px;
  overflow: auto;
  max-height: calc(100vh - 176px);
}

.TemplateComposer__section {
  background: var(--zp-bg-surface);
  border: 1px solid var(--zp-border-hairline);
  border-radius: var(--zp-radius-lg);
  padding: 16px;
  margin-bottom: 14px;
}

.TemplateComposer__sectionHead {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding-bottom: 12px;
  margin-bottom: 14px;
  border-bottom: 1px solid var(--zp-border-hairline);

  h2 {
    font-family: var(--zp-font-display);
    font-size: var(--zp-text-lg);
    line-height: var(--zp-leading-tight);
    letter-spacing: 0;
    margin: 2px 0 0;
  }
}

.TemplateComposer__readout {
  color: var(--zp-text-tertiary);
  font-size: var(--zp-text-xs);
  line-height: 1.5;
  max-width: 280px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.TemplateComposer__typeGrid,
.TemplateComposer__fieldGrid,
.TemplateComposer__toggleGrid,
.TemplateComposer__policyGrid,
.TemplateComposer__preflightGrid {
  display: grid;
  gap: 12px;
}

.TemplateComposer__typeGrid {
  grid-template-columns: repeat(3, minmax(0, 1fr));
  margin-bottom: 14px;
}

.TemplateComposer__type {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 40px;
  border: 1px solid var(--zp-border-default);
  border-radius: var(--zp-radius-md);
  background: var(--zp-bg-elevated);
  color: var(--zp-text-secondary);
  font-family: var(--zp-font-body);
  font-size: var(--zp-text-sm);
  font-weight: 700;
  cursor: pointer;

  &:hover {
    background: var(--zp-bg-subtle);
    color: var(--zp-text-primary);
  }
}

.TemplateComposer__type--active {
  border-color: var(--zp-brand);
  background: var(--zp-brand-soft);
  color: var(--zp-brand);
}

.TemplateComposer__fieldGrid--two {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.TemplateComposer__toggleGrid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.TemplateComposer__switch {
  margin-top: 0 !important;
  padding-top: 0 !important;
}

.TemplateComposer__switch--wide {
  grid-column: 1 / -1;
}

.TemplateComposer__policyGrid,
.TemplateComposer__preflightGrid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.TemplateComposer__policy,
.TemplateComposer__preflight {
  display: grid;
  grid-template-columns: 28px minmax(0, 1fr);
  gap: 10px;
  min-height: 76px;
  padding: 12px;
  border: 1px solid var(--zp-border-hairline);
  border-radius: var(--zp-radius-md);
  background: var(--zp-bg-elevated);
  color: var(--zp-text-secondary);
  text-align: left;

  strong,
  small {
    display: block;
    min-width: 0;
  }

  strong {
    color: var(--zp-text-primary);
    font-size: var(--zp-text-sm);
    line-height: 1.25;
  }

  small {
    color: var(--zp-text-tertiary);
    font-size: var(--zp-text-xs);
    line-height: 1.4;
    margin-top: 4px;
  }
}

.TemplateComposer__policy {
  cursor: pointer;

  &:hover {
    background: var(--zp-bg-subtle);
  }
}

.TemplateComposer__policy--active {
  border-color: var(--zp-brand);
  background: var(--zp-brand-soft);
}

.TemplateComposer__policyIcon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: 1px solid var(--zp-border-default);
  border-radius: var(--zp-radius-sm);
  background: var(--zp-bg-surface);
  color: var(--zp-text-tertiary);
}

.TemplateComposer__policy--active .TemplateComposer__policyIcon {
  border-color: transparent;
  background: var(--zp-status-success-bg);
  color: var(--zp-status-success);
}

.TemplateComposer__policyNote {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  margin-top: 12px;
  padding: 10px 12px;
  border-radius: var(--zp-radius-md);
  background: var(--zp-status-info-bg);
  color: var(--zp-status-info);
  font-size: var(--zp-text-xs);
  line-height: 1.45;
}

.TemplateComposer__preflight--ok {
  border-color: var(--zp-status-success-bg);

  > svg,
  > iconify-icon {
    color: var(--zp-status-success);
  }
}

.TemplateComposer__preflight--warning {
  border-color: var(--zp-status-warning-bg);

  > svg,
  > iconify-icon {
    color: var(--zp-status-warning);
  }
}

.TemplateComposer__preflight--neutral {
  > svg,
  > iconify-icon {
    color: var(--zp-status-info);
  }
}

.TemplateComposer__helpIcon {
  cursor: pointer;
  color: var(--zp-text-tertiary);
}

.TemplateComposer__inlineAlert {
  margin-top: 2px;
}

.TemplateComposer__args {
  position: relative;
  margin-top: 2px;
}

.TemplateComposer__argsJson {
  position: absolute;
  right: -10px;
  top: -14px;
}

.TemplateComposer__summaryBlock {
  padding: 0 0 16px;
  margin-bottom: 16px;
  border-bottom: 1px solid var(--zp-border-hairline);
}

.TemplateComposer__command {
  margin: 10px 0 0;
  padding: 12px;
  white-space: pre-wrap;
  word-break: break-word;
  background: var(--zp-bg-surface);
  border: 1px solid var(--zp-border-hairline);
  border-radius: var(--zp-radius-md);
  color: var(--zp-text-primary);
  font-size: var(--zp-text-xs);
  line-height: 1.55;
}

.TemplateComposer__checks {
  list-style: none;
  margin: 10px 0 0;
  padding: 0;
  display: grid;
  gap: 8px;

  li {
    display: flex;
    align-items: center;
    gap: 8px;
    color: var(--zp-text-tertiary);
    font-size: var(--zp-text-sm);
  }
}

.TemplateComposer__check--ok {
  color: var(--zp-text-primary) !important;

  svg,
  iconify-icon {
    color: var(--zp-status-success);
  }
}

.TemplateComposer__resourceList {
  margin: 10px 0 0;

  div {
    display: grid;
    gap: 4px;
    padding: 7px 0;
    border-bottom: 1px solid var(--zp-border-hairline);
  }

  div:last-child {
    border-bottom: 0;
  }

  dt {
    color: var(--zp-text-tertiary);
    font-size: var(--zp-text-xs);
    text-transform: uppercase;
    letter-spacing: var(--zp-tracking-overline);
    font-weight: 700;
    line-height: 1.25;
  }

  dd {
    min-width: 0;
    margin: 0;
    color: var(--zp-text-primary);
    font-size: var(--zp-text-sm);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.TemplateComposer__capability,
.TemplateComposer__signal {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 7px 0;
  border-bottom: 1px solid var(--zp-border-hairline);
  color: var(--zp-text-tertiary);
  font-size: var(--zp-text-xs);
}

.TemplateComposer__capability {
  justify-content: flex-start;

  svg,
  iconify-icon {
    color: var(--zp-text-tertiary);
  }
}

.TemplateComposer__capability--active {
  color: var(--zp-text-primary);

  svg,
  iconify-icon {
    color: var(--zp-status-success);
  }
}

.TemplateComposer__signal strong {
  min-width: 0;
  color: var(--zp-text-primary);
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.TemplateComposer__risk {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  margin-top: 8px;
  padding: 8px;
  border-radius: var(--zp-radius-md);
  background: var(--zp-status-warning-bg);
  color: var(--zp-status-warning);
  font-size: var(--zp-text-xs);
  line-height: 1.45;
}

.TemplateComposer {
  .v-input {
    font-size: var(--zp-text-sm);
  }

  .v-input__slot {
    min-height: 38px !important;
  }

  fieldset {
    border-radius: var(--zp-radius-md) !important;
    border-color: var(--zp-border-hairline) !important;
    background: var(--zp-bg-elevated) !important;
  }

  legend {
    color: var(--zp-text-tertiary);
    font-family: var(--zp-font-body);
    font-size: var(--zp-text-xs);
    letter-spacing: var(--zp-tracking-overline);
    text-transform: uppercase;
    font-weight: 700;
  }
}

.EditTemplateDialog {
  width: min(1760px, calc(100vw - 48px));
  max-width: calc(100vw - 48px) !important;

  .v-card__title {
    border-bottom: 1px solid var(--zp-border-hairline);
    padding: 14px 20px !important;
  }

  .v-card__actions {
    border-top: 1px solid var(--zp-border-hairline);
    padding: 10px 16px;
  }
}

@media (max-width: 1180px) {
  .TemplateComposer__grid {
    grid-template-columns: 190px minmax(0, 1fr);
  }

  .TemplateComposer__summary {
    grid-column: 1 / -1;
    border-left: 0;
    border-top: 1px solid var(--zp-border-hairline);
  }
}

@media (max-width: 760px) {
  .EditTemplateDialog {
    width: calc(100vw - 32px);
    max-width: calc(100vw - 32px) !important;
  }

  .TemplateComposer__grid,
  .TemplateComposer__fieldGrid--two,
  .TemplateComposer__typeGrid,
  .TemplateComposer__toggleGrid,
  .TemplateComposer__policyGrid,
  .TemplateComposer__preflightGrid {
    grid-template-columns: 1fr;
  }

  .TemplateComposer__rail {
    border-right: 0;
    border-bottom: 1px solid var(--zp-border-hairline);
  }

  .TemplateComposer__main {
    max-height: none;
  }
}
</style>
<script>
/* eslint-disable import/no-extraneous-dependencies,import/extensions */

import axios from 'axios';

import ItemFormBase from '@/components/ItemFormBase';
import 'codemirror/lib/codemirror.css';
import 'codemirror/mode/vue/vue.js';
import 'codemirror/addon/lint/json-lint.js';
import 'codemirror/addon/display/placeholder.js';
import ArgsPicker from '@/components/ArgsPicker.vue';
import TemplateVaults from '@/components/TemplateVaults.vue';
import { TEMPLATE_TYPE_ICONS, TEMPLATE_TYPE_TITLES } from '@/lib/constants';
import AppFieldsMixin from '@/components/AppFieldsMixin';
import AppsMixin from '@/components/AppsMixin';
import RichEditor from '@/components/RichEditor.vue';
import SurveyVars from './SurveyVars';

export default {
  mixins: [ItemFormBase, AppFieldsMixin, AppsMixin],

  components: {
    RichEditor,
    TemplateVaults,
    ArgsPicker,
    SurveyVars,
  },

  props: {
    sourceItemId: Number,
    app: String,
    premiumFeatures: Object,
    taskType: String,
  },

  data() {
    return {
      cronFormats: [{
        cron: '* * * * *',
        title: '1 minute',
      }, {
        cron: '*/5 * * * *',
        title: '5 minutes',
      }, {
        cron: '*/10 * * * *',
        title: '10 minutes',
      }, {
        cron: '@hourly',
        title: '1 hour',
      }, {
        cron: '@daily',
        title: '24 hours',
      }],
      itemTypeIndex: 0,
      TEMPLATE_TYPE_ICONS,
      TEMPLATE_TYPE_TITLES,
      cmOptions: {
        tabSize: 2,
        mode: 'application/json',
        lineNumbers: true,
        line: true,
        lint: true,
        indentWithTabs: false,
      },
      item: {
        task_params: {},
      },
      inventory: null,
      repositories: null,
      environment: null,
      executionEnvironments: null,
      views: null,
      schedules: null,
      buildTemplates: null,
      cronFormat: '* * * * *',
      cronRepositoryId: null,
      cronVisible: false,

      helpDialog: null,
      helpKey: null,

      args: [],
      runnerTags: null,
      branches: null,
      setBranch: false,

      playbookFiles: [],
      loadingPlaybookFiles: false,
      playbookFilesError: '',
      playbookFilesDebounceTimer: null,
    };
  },

  watch: {
    gitBranch() {
      this.setBranch = false;
    },

    async repositoryId() {
      this.branches = null;

      await this.loadBranches();
      this.schedulePlaybookFilesLoad();
    },

    'item.git_branch': function reloadFilesOnBranch() {
      this.schedulePlaybookFilesLoad();
    },

    needReset(val) {
      if (val) {
        if (this.item != null) {
          this.item.template_id = this.templateId;
        }
        this.inventory = null;
      }
    },

    sourceItemId(val) {
      this.item.template_id = val;
    },

    itemTypeIndex(val) {
      const key = Object.keys(TEMPLATE_TYPE_ICONS)[val] || '';
      this.item.type = key;
    },

    // Clear execution_environment_id when the user switches away from Ansible
    // so a stale id isn't silently persisted on non-Ansible templates (the
    // runtime only honours EE on AppAnsible anyway).
    'item.app': function onAppChange(newApp) {
      if (this.item && newApp !== 'ansible' && newApp !== '') {
        this.item.execution_environment_id = null;
      }
    },
  },

  async created() {
    await this.loadBranches();
    await this.loadPlaybookFiles();
  },

  computed: {
    argsJson: {
      get() {
        return JSON.stringify(this.args);
      },
      set(val) {
        this.args = JSON.parse(val);
      },
    },

    repositoryId() {
      return this.item?.repository_id;
    },

    gitBranch() {
      return this.item?.git_branch;
    },

    // Terraform-only: reuse the playbook file list filtered to .tf files
    // so the override_backend filename picker has matching suggestions.
    terraformBackendFiles() {
      return this.playbookFiles.filter((f) => f.endsWith('.tf') || f.endsWith('.tf.json'));
    },

    // Treat both 'ansible' and the legacy empty string as Ansible. The
    // backend (db/Template.go) maps `case AppAnsible, "":` to ansible, so
    // hiding the EE field on app==='' would lock legacy templates out.
    isAnsibleTemplate() {
      const app = this.item?.app;
      return app === 'ansible' || app === '' || app == null;
    },

    selectedInventoryType() {
      if (!Array.isArray(this.inventory) || this.item?.inventory_id == null) {
        return null;
      }
      const found = this.inventory.find((i) => i.id === this.item.inventory_id);
      return found ? found.type : null;
    },

    allow_override_inventory: {
      get() {
        return this.item.task_params.allow_override_inventory;
      },
      set(newValue) {
        this.item.task_params.allow_override_inventory = newValue;
      },
    },

    typeKeys() {
      return Object.keys(TEMPLATE_TYPE_ICONS);
    },

    selectedTypeTitle() {
      return this.$t(TEMPLATE_TYPE_TITLES[this.item?.type || '']);
    },

    appTitle() {
      return this.getAppTitle(this.app, true) || 'Ansible';
    },

    appIconColor() {
      const color = this.getAppColor(this.app);
      return color === 'black' || color === 'white' ? 'var(--zp-text-primary)' : color;
    },

    identityComplete() {
      if (!this.item?.name) {
        return false;
      }
      if (this.item.type === 'build' && !this.item.start_version) {
        return false;
      }
      if (this.item.type === 'deploy' && !this.item.build_template_id) {
        return false;
      }
      return true;
    },

    sourceComplete() {
      return this.readinessChecks
        .filter((check) => ['repository', 'playbook', 'inventory', 'environment'].includes(check.key))
        .every((check) => check.ok);
    },

    runtimeComplete() {
      return !this.cronVisible || !!this.cronRepositoryId;
    },

    launchPolicyStep() {
      return this.needAppBlock ? '05' : '04';
    },

    surveyStep() {
      return this.needAppBlock ? '06' : '05';
    },

    reviewStep() {
      return this.needAppBlock ? '07' : '06';
    },

    formSections() {
      return [{
        id: 'identity',
        icon: 'badge-check',
        title: this.$t('templateComposerIdentity'),
        meta: this.selectedTypeTitle,
        complete: this.identityComplete,
      }, {
        id: 'source',
        icon: 'git-branch',
        title: this.$t('templateComposerSource'),
        meta: this.sourceReadout,
        complete: this.sourceComplete,
      }, {
        id: 'runtime',
        icon: 'sliders-horizontal',
        title: this.$t('templateComposerRuntime'),
        meta: this.runtimeReadout,
        complete: this.runtimeComplete,
      }, {
        id: 'launch-policy',
        icon: 'shield',
        title: this.$t('templateComposerLaunchPolicy'),
        meta: this.promptControlReadout,
        complete: true,
      }, {
        id: 'survey',
        icon: 'list-plus',
        title: this.$t('surveyVariables'),
        meta: this.surveyCountLabel,
        complete: true,
      }, {
        id: 'review',
        icon: 'shield-check',
        title: this.$t('templateComposerReview'),
        meta: `${this.readyCount}/${this.readinessChecks.length}`,
        complete: this.readinessChecks.every((check) => check.ok),
      }];
    },

    promptControls() {
      const controls = [{
        key: 'args',
        icon: 'terminal',
        title: this.$t('allowCliArgsInTask'),
        description: this.$t('templateComposerPromptArgsDescription'),
        target: 'allow_override_args_in_task',
        scope: 'item',
        enabled: !!this.item?.allow_override_args_in_task,
      }, {
        key: 'branch',
        icon: 'git-branch',
        title: this.$t('allow_override_branch'),
        description: this.$t('templateComposerPromptBranchDescription'),
        target: 'allow_override_branch_in_task',
        scope: 'item',
        enabled: !!this.item?.allow_override_branch_in_task,
      }];

      if (this.needField('allow_override_inventory')) {
        controls.push({
          key: 'inventory',
          icon: 'server',
          title: this.$t('allowInventoryInTask'),
          description: this.$t('templateComposerPromptInventoryDescription'),
          target: 'allow_override_inventory',
          scope: 'task_params',
          enabled: !!this.allow_override_inventory,
        });
      }

      if (this.needField('allow_override_limit')) {
        controls.push({
          key: 'limit',
          icon: 'crosshair',
          title: this.$t('allowLimitInTask'),
          description: this.$t('templateComposerPromptLimitDescription'),
          target: 'allow_override_limit',
          scope: 'task_params',
          enabled: !!this.item?.task_params?.allow_override_limit,
        });
      }

      if (this.needField('allow_override_tags')) {
        controls.push({
          key: 'tags',
          icon: 'tags',
          title: this.$t('tags'),
          description: this.$t('templateComposerPromptTagsDescription'),
          target: 'allow_override_tags',
          scope: 'task_params',
          enabled: !!this.item?.task_params?.allow_override_tags,
        });
      }

      if (this.needField('allow_override_skip_tags')) {
        controls.push({
          key: 'skip_tags',
          icon: 'badge-x',
          title: this.$t('skipTags'),
          description: this.$t('templateComposerPromptSkipTagsDescription'),
          target: 'allow_override_skip_tags',
          scope: 'task_params',
          enabled: !!this.item?.task_params?.allow_override_skip_tags,
        });
      }

      if (this.needField('allow_debug')) {
        controls.push({
          key: 'debug',
          icon: 'bug',
          title: this.$t('allowDebug'),
          description: this.$t('templateComposerPromptDebugDescription'),
          target: 'allow_debug',
          scope: 'task_params',
          enabled: !!this.item?.task_params?.allow_debug,
        });
      }

      if (this.needField('allow_auto_approve')) {
        controls.push({
          key: 'auto_approve',
          icon: 'check-check',
          title: this.$t('auto_approve'),
          description: this.$t('templateComposerPromptAutoApproveDescription'),
          target: 'allow_auto_approve',
          scope: 'task_params',
          enabled: !!this.item?.task_params?.allow_auto_approve,
        });
      }

      if (this.needField('skip_galaxy_preflight')) {
        controls.push({
          key: 'skip_galaxy_preflight',
          icon: 'package-x',
          title: this.$t('skipGalaxyPreflight'),
          description: this.$t('skipGalaxyPreflightHint'),
          target: 'skip_galaxy_preflight',
          scope: 'task_params',
          enabled: !!this.item?.task_params?.skip_galaxy_preflight,
        });
      }

      return controls;
    },

    promptControlReadout() {
      const enabled = this.promptControls.filter((control) => control.enabled).length;
      return this.$t('templateComposerPromptCount', {
        enabled,
        total: this.promptControls.length,
      });
    },

    operatorCapabilities() {
      return this.promptControls.map((control) => ({
        label: control.title,
        enabled: control.enabled,
      }));
    },

    automationSignals() {
      return [{
        label: this.$t('templateComposerSchedule'),
        value: this.cronVisible ? this.cronFormat : this.$t('templateComposerManualOnly'),
      }, {
        label: this.$t('runner_tag'),
        value: this.item?.runner_tag || this.$t('templateComposerAnyRunner'),
      }, {
        label: this.$t('suppressSuccessAlerts'),
        value: this.item?.suppress_success_alerts ? this.$t('yes') : this.$t('no'),
      }, {
        label: this.$t('allow_parallel_tasks'),
        value: this.item?.allow_parallel_tasks ? this.$t('yes') : this.$t('no'),
      }];
    },

    preflightChecks() {
      const checks = [{
        key: 'repository',
        title: this.$t('templateComposerPreflightRepository'),
        description: this.item?.repository_id
          ? this.sourceReadout
          : this.$t('templateComposerPreflightRepositoryMissing'),
        state: this.item?.repository_id ? 'ok' : 'warning',
        icon: this.item?.repository_id ? 'check' : 'triangle-alert',
      }];

      if (this.needField('playbook')) {
        const playbookKnown = this.item?.playbook
          && this.playbookFiles.includes(this.item.playbook);
        const playbookOk = !!this.item?.playbook && !this.playbookFilesError;
        checks.push({
          key: 'entrypoint',
          title: this.$t('templateComposerPreflightEntrypoint'),
          description: this.playbookPreflightDescription(playbookKnown),
          state: playbookOk ? 'ok' : 'warning',
          icon: playbookOk ? 'check' : 'triangle-alert',
        });
      }

      if (this.needField('inventory')) {
        checks.push({
          key: 'inventory',
          title: this.$t('templateComposerPreflightInventory'),
          description: this.item?.inventory_id
            ? this.resourceName(this.inventory, this.item.inventory_id)
            : this.$t('templateComposerPreflightInventoryMissing'),
          state: this.item?.inventory_id ? 'ok' : 'warning',
          icon: this.item?.inventory_id ? 'check' : 'triangle-alert',
        });
      }

      if (this.isAnsibleTemplate) {
        const needsEe = this.selectedInventoryType === 'dynamic'
          && this.item.execution_environment_id == null;
        checks.push({
          key: 'execution_environment',
          title: this.$t('executionEnvironmentSingular'),
          description: needsEe
            ? this.$t('dynamicInventoryRequiresEE')
            : this.resourceName(
              this.executionEnvironments,
              this.item?.execution_environment_id,
            ),
          state: needsEe ? 'warning' : 'neutral',
          icon: needsEe ? 'triangle-alert' : 'container',
        });
      }

      checks.push({
        key: 'cron',
        title: this.$t('templateComposerPreflightSchedule'),
        description: this.cronVisible
          ? this.$t('templateComposerPreflightScheduleEnabled')
          : this.$t('templateComposerManualOnly'),
        state: this.runtimeComplete ? 'ok' : 'warning',
        icon: this.runtimeComplete ? 'check' : 'triangle-alert',
      });

      checks.push({
        key: 'operator',
        title: this.$t('templateComposerPreflightOperatorControls'),
        description: this.promptControlReadout,
        state: 'neutral',
        icon: 'sliders-horizontal',
      });

      return checks;
    },

    sourceReadout() {
      const repo = this.resourceName(this.repositories, this.item?.repository_id);
      const branch = this.item?.git_branch || this.$t('templateComposerDefaultBranch');
      return repo === '—' ? this.$t('templateComposerNoRepository') : `${repo} · ${branch}`;
    },

    runtimeReadout() {
      const parts = [];
      if (this.item?.runner_tag) {
        parts.push(this.item.runner_tag);
      }
      if (this.item?.allow_parallel_tasks) {
        parts.push(this.$t('templateComposerParallel'));
      }
      if (this.cronVisible) {
        parts.push(this.cronFormat || 'cron');
      }
      return parts.length > 0 ? parts.join(' · ') : this.$t('templateComposerDefaultRuntime');
    },

    surveyCountLabel() {
      const count = (this.item?.survey_vars || []).length;
      return this.$t('templateComposerSurveyCount', { count });
    },

    readyCount() {
      return this.readinessChecks.filter((check) => check.ok).length;
    },

    readinessChecks() {
      const checks = [{
        key: 'name',
        label: this.$t('templateComposerCheckName'),
        ok: !!this.item?.name,
      }];

      if (this.item?.type === 'build') {
        checks.push({
          key: 'start_version',
          label: this.$t('templateComposerCheckStartVersion'),
          ok: !!this.item?.start_version,
        });
      }

      if (this.item?.type === 'deploy') {
        checks.push({
          key: 'build_template',
          label: this.$t('templateComposerCheckBuildTemplate'),
          ok: !!this.item?.build_template_id,
        });
      }

      if (this.needField('repository')) {
        checks.push({
          key: 'repository',
          label: this.$t('templateComposerCheckRepository'),
          ok: !!this.item?.repository_id,
        });
      }

      if (this.needField('playbook') && this.isFieldRequired('playbook')) {
        checks.push({
          key: 'playbook',
          label: this.$t('templateComposerCheckEntrypoint'),
          ok: !!this.item?.playbook,
        });
      }

      if (this.needField('inventory') && this.isFieldRequired('inventory')) {
        checks.push({
          key: 'inventory',
          label: this.$t('templateComposerCheckInventory'),
          ok: !!this.item?.inventory_id,
        });
      }

      if (this.needField('environment') && this.isFieldRequired('environment')) {
        checks.push({
          key: 'environment',
          label: this.$t('templateComposerCheckEnvironment'),
          ok: Array.isArray(this.item?.environment_ids) && this.item.environment_ids.length > 0,
        });
      }

      if (this.cronVisible) {
        checks.push({
          key: 'cron_repository',
          label: this.$t('templateComposerCheckCronRepository'),
          ok: !!this.cronRepositoryId,
        });
      }

      return checks;
    },

    commandPreview() {
      const entry = this.item?.playbook || this.$t('templateComposerEntrypointPlaceholder');
      const args = this.args.length > 0 ? ` ${this.args.join(' ')}` : '';
      const branch = this.item?.git_branch ? ` # ${this.item.git_branch}` : '';

      switch (this.app) {
        case 'terraform':
          return `terraform -chdir=${entry || '.'} ${this.item?.type === 'deploy' ? 'apply' : 'plan'}${args}${branch}`;
        case 'tofu':
          return `tofu -chdir=${entry || '.'} ${this.item?.type === 'deploy' ? 'apply' : 'plan'}${args}${branch}`;
        case 'terragrunt':
          return `terragrunt ${this.item?.type === 'deploy' ? 'apply' : 'plan'} --terragrunt-working-dir ${entry || '.'}${args}${branch}`;
        case 'bash':
          return `bash ${entry}${args}${branch}`;
        case 'python':
          return `python ${entry}${args}${branch}`;
        case 'powershell':
          return `pwsh -File ${entry}${args}${branch}`;
        case 'pulumi':
          return `pulumi ${this.item?.type === 'deploy' ? 'up' : 'preview'} --cwd ${entry || '.'}${args}${branch}`;
        case '':
        case 'ansible':
        default:
          return `ansible-playbook ${entry}${args}${branch}`;
      }
    },

    resourceSummary() {
      return [{
        label: this.$t('repository'),
        value: this.resourceName(this.repositories, this.item?.repository_id),
      }, {
        label: this.$t('branch'),
        value: this.item?.git_branch || this.$t('templateComposerDefaultBranch'),
      }, {
        label: this.fieldLabel('inventory'),
        value: this.resourceName(this.inventory, this.item?.inventory_id),
      }, {
        label: this.$t('executionEnvironmentSingular'),
        value: this.resourceName(this.executionEnvironments, this.item?.execution_environment_id),
      }, {
        label: this.fieldLabel('environment'),
        value: this.environmentSummary,
      }];
    },

    environmentSummary() {
      if (!Array.isArray(this.item?.environment_ids) || this.item.environment_ids.length === 0) {
        return '—';
      }
      return this.item.environment_ids
        .map((id) => this.resourceName(this.environment, id))
        .join(', ');
    },

    riskHints() {
      const hints = [];
      if (this.isAnsibleTemplate && this.selectedInventoryType === 'dynamic'
        && this.item.execution_environment_id == null) {
        hints.push(this.$t('dynamicInventoryRequiresEE'));
      }
      if (this.item?.allow_override_args_in_task) {
        hints.push(this.$t('templateComposerRiskArgs'));
      }
      if (this.allow_override_inventory) {
        hints.push(this.$t('templateComposerRiskInventory'));
      }
      if (this.item?.allow_parallel_tasks) {
        hints.push(this.$t('templateComposerRiskParallel'));
      }
      return hints;
    },

    loaderHeight() {
      switch (this.taskType) {
        case 'build':
          if (['', 'ansible', 'terraform', 'tofu'].includes(this.app)) {
            return 626;
          }
          return 560;
        case 'deploy':
          if (['', 'ansible', 'terraform', 'tofu'].includes(this.app)) {
            return 676;
          }
          return 610;
        default:
          if (['', 'ansible', 'terraform', 'tofu'].includes(this.app)) {
            return 564;
          }
          return 514;
      }
    },

    appBlockTitle() {
      switch (this.app) {
        case '':
        case 'ansible':
          return this.$t('ansible_playbook_options');
        default:
          return this.app;
      }
    },

    needAppBlock() {
      return ['', 'ansible', 'tofu', 'terraform', 'terragrunt'].includes(this.app);
    },

    surveyVars() {
      // if (this.sourceItemId != null && this.item.survey_vars === undefined) {
      //   throw new Error();
      // }
      return this.item.survey_vars;
    },

    vaults() {
      // if (this.sourceItemId != null && this.item.vaults === undefined) {
      //   throw new Error();
      // }
      return this.item.vaults;
    },

    isLoaded() {
      // if (this.isNew && this.sourceItemId == null) {
      //   return true;
      // }

      return this.repositories != null
        && this.inventory != null
        && this.environment != null
        && this.item != null
        && this.schedules != null
        && this.views != null
        && this.runnerTags != null;
    },

  },

  methods: {
    async loadBranches() {
      if (this.repositoryId == null) {
        return;
      }

      this.branches = await this.loadProjectEndpoint(
        `/repositories/${this.repositoryId}/branches`,
      );
    },

    schedulePlaybookFilesLoad() {
      // Debounce so typing in the branch field doesn't fire a clone per keystroke.
      if (this.playbookFilesDebounceTimer) {
        clearTimeout(this.playbookFilesDebounceTimer);
      }
      this.playbookFilesDebounceTimer = setTimeout(() => this.loadPlaybookFiles(), 350);
    },

    async loadPlaybookFiles() {
      this.playbookFilesError = '';
      if (!this.repositoryId) {
        this.playbookFiles = [];
        return;
      }

      this.loadingPlaybookFiles = true;
      try {
        const params = new URLSearchParams();
        if (this.item.git_branch) params.set('ref', this.item.git_branch);
        // `item.app` is only populated on save (see beforeSave). The actual
        // template type lives in the `app` prop, so use that for filtering.
        const appType = this.app || this.item.app;
        if (appType) params.set('app', appType);
        const qs = params.toString();
        const res = await this.loadProjectEndpoint(
          `/repositories/${this.repositoryId}/files${qs ? `?${qs}` : ''}`,
        );
        if (Array.isArray(res)) {
          this.playbookFiles = res;
        } else if (res && res.error) {
          // Backend wraps the failure so the form stays editable — keep the
          // free-typing combobox, just surface why suggestions are missing.
          this.playbookFiles = [];
          this.playbookFilesError = res.error;
        } else {
          this.playbookFiles = [];
        }
      } catch (err) {
        this.playbookFiles = [];
        this.playbookFilesError = err.response?.data?.error || err.message || 'Failed to load files';
      } finally {
        this.loadingPlaybookFiles = false;
      }
    },

    validateBackendFilename(v) {
      if (!v) {
        return true;
      }

      if (!v.endsWith('.tf')) {
        return 'File must have extension .tf';
      }

      return /^[a-zA-Z0-9_\-.]+\.tf$/.test(v);
    },

    setSkipTags(tags) {
      this.item.task_params.skip_tags = tags;
    },

    setTags(tags) {
      this.item.task_params.tags = tags;
    },

    setLimit(limit) {
      this.item.task_params.limit = limit;
    },

    setArgs(args) {
      this.args = args;
    },

    setSurveyVars(v) {
      this.item.survey_vars = v;
    },

    setTemplateVaults(v) {
      this.item.vaults = v;
    },

    togglePromptControl(control) {
      const nextValue = !control.enabled;
      if (control.scope === 'task_params') {
        this.$set(this.item.task_params, control.target, nextValue);
        return;
      }

      this.$set(this.item, control.target, nextValue);
    },

    playbookPreflightDescription(playbookKnown) {
      if (!this.item?.playbook) {
        return this.$t('templateComposerPreflightEntrypointMissing');
      }
      if (playbookKnown) {
        return this.$t('templateComposerPreflightEntrypointMatched');
      }
      if (this.playbookFilesError) {
        return this.playbookFilesError;
      }
      return this.$t('templateComposerPreflightEntrypointManual');
    },

    resourceName(items, id) {
      if (id == null || !Array.isArray(items)) {
        return '—';
      }
      const found = items.find((item) => item.id === id);
      return found ? (found.name || found.title || found.tag || `#${id}`) : '—';
    },

    showHelpDialog(key) {
      this.helpKey = key;
      this.helpDialog = true;
    },

    getNewItem() {
      return {
        task_params: {},
        environment_ids: [],
        execution_environment_id: null,
      };
    },

    async loadRelativeData() {
      let templates;
      let inventory1;
      let inventory2;

      [
        this.repositories,
        inventory1,
        inventory2,
        this.schedules,
        this.views,
        this.environment,
        this.executionEnvironments,
        templates,
        this.runnerTags,
      ] = await Promise.all([
        this.loadProjectResources('repositories'),
        this.loadProjectEndpoint(`/inventory?app=${this.app}&template_id=${this.itemId}`),
        this.loadProjectEndpoint(`/inventory?app=${this.app}`),
        this.isNew ? [] : this.loadProjectEndpoint(`/templates/${this.itemId}/schedules`),
        this.loadProjectResources('views'),
        this.loadProjectResources('environment'),
        this.loadProjectResources('execution_environments'),
        this.loadProjectResources('templates'),
        this.loadProjectResources('runner_tags'),
      ]);

      this.inventory = [...inventory1, ...inventory2];

      const builds = [];
      const deploys = [];

      templates.forEach((t) => {
        switch (t.type) {
          case 'build':
            if (builds.length === 0) {
              builds.push({ header: 'Build Templates' });
            }
            builds.push(t);
            break;
          case 'deploy':
            if (deploys.length === 0) {
              deploys.push({ header: 'Deploy Templates' });
            }
            deploys.push(t);
            break;
          default:
            break;
        }
      });

      this.buildTemplates = builds;
      if (this.buildTemplates.length > 0 && deploys.length > 0) {
        this.buildTemplates.push({ divider: true });
      }
      this.buildTemplates.push(...deploys);
    },

    async afterLoadData() {
      if (this.sourceItemId) {
        const item = await this.loadProjectResource('templates', this.sourceItemId);

        item.id = null;

        if (item.vaults) {
          for (let i = 0; i < item.vaults.length; i += 1) {
            item.vaults[i].id = null;
          }
        }

        const sourceSchedule = (await this.loadProjectEndpoint(`/templates/${this.sourceItemId}/schedules`))[0];

        if (sourceSchedule != null) {
          this.cronFormat = sourceSchedule.cron_format;
          this.cronRepositoryId = sourceSchedule.repository_id;
          this.cronVisible = this.cronRepositoryId != null;
        }

        this.item = item;
      }

      if (!this.item.task_params) {
        this.item.task_params = {};
      }

      if (!Array.isArray(this.item.environment_ids)) {
        this.$set(this.item, 'environment_ids', []);
      }

      this.args = JSON.parse(this.item.arguments || '[]');

      await this.loadRelativeData();

      if (this.schedules.length > 0) {
        const schedule = this.schedules.find((s) => s.repository_id != null);
        if (schedule != null) {
          this.cronFormat = schedule.cron_format;
          this.cronRepositoryId = schedule.repository_id;
          this.cronVisible = this.cronRepositoryId != null;
        }
      }

      this.itemTypeIndex = Math.max(0, Object.keys(TEMPLATE_TYPE_ICONS).indexOf(this.item.type || ''));
    },

    getItemsUrl() {
      return `/api/project/${this.projectId}/templates`;
    },

    getSingleItemUrl() {
      return `/api/project/${this.projectId}/templates/${this.itemId}`;
    },

    async beforeSave() {
      if (this.cronFormat == null || this.cronFormat === '') {
        return;
      }

      await axios({
        method: 'post',
        url: `/api/project/${this.projectId}/schedules/validate`,
        responseType: 'json',
        data: {
          cron_format: this.cronFormat,
        },
      });

      this.item.app = this.app;

      this.item.arguments = JSON.stringify(this.args);
    },

    async afterSave(newItem) {
      if (newItem || this.schedules.length === 0) {
        if (this.cronFormat != null && this.cronFormat !== '' && this.cronVisible) {
          // new schedule
          await axios({
            method: 'post',
            url: `/api/project/${this.projectId}/schedules`,
            responseType: 'json',
            data: {
              project_id: this.projectId,
              template_id: newItem ? newItem.id : this.itemId,
              cron_format: this.cronFormat,
              repository_id: this.cronRepositoryId,
              active: true,
            },
          });
        }
      } else if (this.schedules.length > 1) {
        // do nothing
      } else if (this.cronFormat == null || this.cronFormat === '' || !this.cronVisible) {
        // drop schedule
        await axios({
          method: 'delete',
          url: `/api/project/${this.projectId}/schedules/${this.schedules[0].id}`,
          responseType: 'json',
        });
      } else {
        // update schedule
        await axios({
          method: 'put',
          url: `/api/project/${this.projectId}/schedules/${this.schedules[0].id}`,
          responseType: 'json',
          data: {
            id: this.schedules[0].id,
            project_id: this.projectId,
            template_id: this.itemId,
            cron_format: this.cronFormat,
            repository_id: this.cronRepositoryId,
          },
        });
      }
    },
  },
};
</script>
